import { Chip, IconButton, Tooltip, Typography } from '@mui/material';
import type { ChipProps } from '@mui/material';
import { HelpCircle } from 'react-feather';

import { makeStyles } from '@mui/styles';

import {
  BillingType,
  GymType,
  ContactType,
  PaymentStatus,
  PaymentHistoryStatus,
  PaymentHistoryDisplayStatus,
  PlanStatus,
  PlanType,
  MembershipStatus,
  ActiveMembershipTypes,
  ClassType,
  PlanDate,
  InvoiceStatus,
  PaymentHistoryStatusMap,
} from '~/services/types';

import { UserRolePermission } from '~/contexts/role-authentication';

type StatusType = {
  contactType: ContactType;
  payment: PaymentStatus;
  planStatus: PlanStatus;
  paymentStatus: PaymentStatus;
  paymentHistoryStatus: PaymentHistoryDisplayStatus;
  billingType: BillingType;
  planType: PlanType;
  staffMemberRole: UserRolePermission;
  gymType: GymType;
  membershipStatus: MembershipStatus;
  activeMembershipTypes: ActiveMembershipTypes;
  classType: ClassType;
  planDate: PlanDate;
  invoiceStatus: InvoiceStatus;
};

const colors: Record<keyof StatusType, Record<string | number, string>> = {
  classType: {
    [ClassType.Bootcamp]: 'colors.blue',
    [ClassType.Boxing]: 'colors.rose',
    [ClassType.Cardio]: 'colors.red',
    [ClassType.Crossfit]: 'colors.cyan',
    [ClassType.Hiit]: 'colors.emerald',
    [ClassType.MartialArts]: 'colors.orange',
    [ClassType.SemiPrivate]: 'colors.fuchsia',
    [ClassType.Spinning]: 'colors.lime',
    [ClassType.StrengthTraining]: 'colors.sky',
    [ClassType.YogaPilates]: 'colors.teal',
  },
  contactType: {
    [ContactType.Lead]: 'colors.orange',
    [ContactType.Client]: 'success',
    [ContactType.Frozen]: 'colors.blue',
    [ContactType.Exited]: 'error',
  },
  payment: {
    [PaymentStatus.Current]: 'success',
    [PaymentStatus.PastDue]: 'error',
    [PaymentStatus.NoneOnFile]: 'warning',
  },
  activeMembershipTypes: {
    [ActiveMembershipTypes.FullMembership]: 'success',
    [ActiveMembershipTypes.None]: 'colors.blue',
    [ActiveMembershipTypes.LimitedTimePass]: 'warning',
    [ActiveMembershipTypes.Challenger]: 'colors.orange',
  },

  membershipStatus: {
    [MembershipStatus.Active]: 'success',
    [MembershipStatus.Frozen]: 'colors.blue',
    [MembershipStatus.EndingSoon]: 'success',
    [MembershipStatus.Cancelled]: 'error',
    [MembershipStatus.NotStarted]: 'success',
  },
  planStatus: {
    [PlanStatus.Active]: 'success',
    [PlanStatus.Draft]: 'colors.gray',
    [PlanStatus.Grandfathered]: 'colors.orange',
    [PlanStatus.Membership]: 'success',
    [PlanStatus.PaidInFull]: 'success',
    [PlanStatus.Challenge]: 'colors.green',
    [PlanStatus.Frozen]: 'colors.blue',
    [PlanStatus.Free]: 'colors.blue',
    [PlanStatus.SessionPacks]: 'colors.purple',
  },
  planType: {
    [PlanType.LargeGroup]: '',
    [PlanType.SemiPrivate]: '',
    [PlanType.PersonalTraining]: '',
    [PlanType.OpenGym]: '',
  },
  paymentStatus: {
    [PaymentStatus.Current]: 'success',
    [PaymentStatus.Successful]: 'success',
    [PaymentStatus.Failed]: 'error',
    [PaymentStatus.PastDue]: 'error',
    [PaymentStatus.NoneOnFile]: 'warning',
    [PaymentStatus.Comped]: 'colors.orange',
    [PaymentStatus.Refunded]: 'colors.blue',
    [PaymentStatus.Forgiven]: 'success',
    [PaymentStatus.PaidToDate]: 'success',
    [PaymentStatus.Cancelled]: 'warning',
    [PaymentStatus.Pending]: 'success',
  },
  paymentHistoryStatus: {
    Pending: 'success', // Approved, Processing, Processed
    Processed: 'success', // Funded, Refunded, Forgiven
    Failed: 'error',
    Cancelled: 'colors.orange',
  } satisfies Record<PaymentHistoryDisplayStatus, string>,
  billingType: {
    [BillingType.PaidInFull]: 'success',
    [BillingType.ClassOrSessionPacks]: 'colors.purple',
    [BillingType.Free]: 'colors.blue',
    [BillingType.Recurring]: 'info',
  },
  staffMemberRole: {
    [UserRolePermission.Owner]: 'colors.green',
    [UserRolePermission.Staff]: 'info',
    [UserRolePermission.Coach]: 'colors.purple',
    [UserRolePermission.Member]: '',
    [UserRolePermission.Manager]: 'success',
  },
  gymType: {
    [GymType.HealthClub]: 'colors.blue',
    [GymType.GroupTrainingOnly]: 'success',
    [GymType.SemiPrivate]: 'warning',
    [GymType.PersonalTraining]: 'error',
    [GymType.MartialArts]: 'colors.orange',
    [GymType.SpinStudio]: 'colors.green',
    [GymType.YogaPilates]: 'colors.skyBlue',
    [GymType.Other]: '',
  },
  planDate: {
    [PlanDate.Cancelled]: 'error',
    [PlanDate.AutoRenewal]: 'success',
    [PlanDate.Frozen]: 'colors.blue',
  },
  invoiceStatus: {
    [InvoiceStatus.Pending]: 'colors.blue',
    [InvoiceStatus.Paid]: 'success',
    [InvoiceStatus.PastDue]: 'success',
    [InvoiceStatus.Void]: 'colors.orange',
    [InvoiceStatus.Posted]: 'success',
  },
};

interface StatusBadgeProps<T extends keyof StatusType> extends ChipProps {
  type: T;
  tooltip?: string | null;
  status: T extends 'paymentHistoryStatus' ? PaymentHistoryStatus | PaymentHistoryDisplayStatus : StatusType[T];
  label?: string | null;
}

const useStyles = makeStyles(() => {
  return {
    withTooltip: {
      position: 'relative',
      display: 'table-cell',
      '& .tooltip': {
        position: 'absolute',
        background: 'white',
        right: 0,
        top: '-10px',
        width: '20px',
        height: '20px',
        padding: 0,
      },
    },
  };
});

export default function StatusBadge<T extends keyof StatusType>({
  status,
  type,
  tooltip = null,
  label = null,
  ...props
}: StatusBadgeProps<T>) {
  let color: string;
  let displayStatus: string;

  if (type === 'paymentHistoryStatus') {
    // Handle both PaymentHistoryStatus and PaymentHistoryDisplayStatus
    if (status in PaymentHistoryStatusMap) {
      // If it's a PaymentHistoryStatus enum value, map it to display status
      const mappedStatus = PaymentHistoryStatusMap[status as PaymentHistoryStatus];
      displayStatus = mappedStatus;
      color = colors.paymentHistoryStatus[mappedStatus] || 'colors.gray';
    } else {
      // If it's already a PaymentHistoryDisplayStatus, use it directly
      displayStatus = status as PaymentHistoryDisplayStatus;
      color = colors.paymentHistoryStatus[displayStatus] || 'colors.gray';
    }
  } else {
    displayStatus = status as string;
    color = colors[type][status];
  }

  const classes = useStyles();

  const displayLabel = label ?? displayStatus ?? 'Unknown';

  return (
    <div className={tooltip ? classes.withTooltip : ''}>
      <Chip
        {...props}
        label={<Typography variant="caption">{displayLabel}</Typography>}
        sx={{
          ...props.sx,
          borderWidth: 1,
          borderColor: `${color}.main`,
          borderStyle: 'solid',
          backgroundColor: `${color}.light`,
        }}
      />
      {tooltip && (
        <Tooltip className="tooltip" title={tooltip}>
          <IconButton>
            <HelpCircle />
          </IconButton>
        </Tooltip>
      )}
    </div>
  );
}
